import type { NavItem, NavSection } from './navigation.models';
import { projectsNavItem } from './project-apps';

/** Puts the Projects entry, built from the primary entries, after Dashboard. */
function withProjects(items: readonly NavItem[]): readonly NavItem[] {
  const at = items.findIndex((item) => item.id === 'dashboard') + 1;

  return [...items.slice(0, at), projectsNavItem(items), ...items.slice(at)];
}

/**
 * The single declaration of the application's primary navigation.
 *
 * Sidebar, mobile drawer and any future command palette all read from here,
 * so adding a destination is a one-line change in one file. Items with
 * children fill the second sidebar pane.
 *
 * A module the workspace has not switched on in the app market is hidden by
 * its `apps` requirement; the backend refuses its endpoints too.
 *
 * The Projects entry is derived from the entries named in `PROJECT_APPS`:
 * the same destinations, opened under `/projects/:projectId`. The Tasker and
 * Schedule entries themselves stay workspace-level (the default project).
 */
export const APP_NAVIGATION: readonly NavSection[] = [
  {
    id: 'primary',
    items: withProjects([
      {
        id: 'dashboard',
        label: 'nav.dashboard',
        icon: '@tui.layout-dashboard',
        route: '/',
        exact: true,
      },
      {
        id: 'learning',
        label: 'nav.learning',
        icon: '@tui.graduation-cap',
        route: '/learning',
        access: { permissions: ['learning:read'], apps: ['learner'] },
        children: [
          {
            id: 'learning-my',
            label: 'nav.learningMy',
            icon: '@tui.book-open',
            route: '/learning/my',
          },
          {
            id: 'learning-courses',
            label: 'nav.learningCourses',
            icon: '@tui.library',
            route: '/learning/courses',
            access: { permissions: ['learning:manage'] },
          },
          {
            id: 'learning-programs',
            label: 'nav.learningPrograms',
            icon: '@tui.layers',
            route: '/learning/programs',
            access: { permissions: ['learning:manage'] },
          },
          {
            id: 'learning-reviews',
            label: 'nav.learningReviews',
            icon: '@tui.star',
            route: '/learning/reviews',
            access: { permissions: ['learning:manage'] },
          },
        ],
      },
      {
        id: 'tasker',
        label: 'nav.tasker',
        icon: '@tui.list-checks',
        route: '/tasker',
        access: { permissions: ['tasker:read'], apps: ['tasker'] },
        children: [
          {
            id: 'tasker-tasks',
            label: 'nav.taskerTasks',
            icon: '@tui.list-todo',
            route: '/tasker/tasks',
            access: { permissions: ['tasker:manage'] },
          },
          {
            id: 'tasker-incidents',
            label: 'nav.taskerIncidents',
            icon: '@tui.siren',
            route: '/tasker/incidents',
            access: { permissions: ['tasker:manage'] },
          },
          {
            id: 'tasker-recurrencies',
            label: 'tk.recurrent.title',
            icon: '@tui.repeat',
            route: '/tasker/recurrencies',
            access: { permissions: ['tasker:manage'] },
          },
          {
            id: 'tasker-templates',
            label: 'nav.taskerTemplates',
            icon: '@tui.layout-template',
            route: '/tasker/templates',
            access: { permissions: ['tasker:manage'] },
          },
          {
            id: 'tasker-my-tasks',
            label: 'nav.taskerMyTasks',
            icon: '@tui.list-checks',
            route: '/tasker/my-tasks',
          },
        ],
      },
      // Workers (employee, contractor, volunteer): the old client's Shifts menu.
      {
        id: 'shifts',
        label: 'nav.shifts',
        icon: '@tui.calendar-days',
        route: '/scheduler',
        access: {
          permissions: ['schedule:read'],
          withoutPermissions: ['schedule:manage'],
          apps: ['scheduler'],
        },
        children: [
          {
            id: 'shifts-my',
            label: 'nav.shifts',
            icon: '@tui.calendar-days',
            route: '/scheduler/my-shifts',
          },
          {
            id: 'shifts-availability',
            label: 's2.availability',
            icon: '@tui.calendar-check',
            route: '/scheduler/my-availability',
          },
          {
            id: 'shifts-swaps',
            label: 'nav.swaps',
            icon: '@tui.arrow-left-right',
            route: '/scheduler/swaps',
          },
          {
            id: 'shifts-open',
            label: 'nav.openShifts',
            icon: '@tui.user-plus',
            route: '/scheduler/open-shifts',
          },
          {
            id: 'shifts-overtime',
            label: 'nav.overtime',
            icon: '@tui.clock-plus',
            route: '/scheduler/overtime',
          },
        ],
      },
      // Whoever plans the schedule and approves the hours.
      {
        id: 'scheduler',
        label: 'nav.schedule',
        icon: '@tui.calendar',
        route: '/scheduler',
        access: { permissions: ['schedule:manage'], anyApps: ['scheduler', 'timeTracking'] },
        children: [
          {
            id: 'scheduler-planning',
            label: 's2.planning',
            icon: '@tui.layout-grid',
            route: '/scheduler/planning',
            access: { apps: ['scheduler'] },
          },
          {
            id: 'scheduler-schedule',
            label: 's2.schedule',
            icon: '@tui.calendar',
            route: '/scheduler/schedule',
            access: { apps: ['scheduler'] },
          },
          {
            id: 'scheduler-availability',
            label: 's2.availability',
            icon: '@tui.user-check',
            route: '/scheduler/availability',
            access: { apps: ['scheduler'] },
          },
          {
            id: 'scheduler-time-tracking',
            label: 's2.timeTracking',
            icon: '@tui.clock',
            route: '/scheduler/time-tracking',
            access: { apps: ['timeTracking'] },
          },
        ],
      },
      {
        id: 'users',
        label: 'nav.users',
        icon: '@tui.users',
        route: '/users',
        access: { permissions: ['users:read'] },
        children: [
          {
            id: 'users-users',
            label: 'users.tabUsers',
            icon: '@tui.users',
            route: '/users/users',
          },
          {
            id: 'users-teams',
            label: 'users.tabTeams',
            icon: '@tui.users-round',
            route: '/users/teams',
          },
          {
            id: 'users-positions',
            label: 'users.tabPositions',
            icon: '@tui.briefcase-business',
            route: '/users/positions',
          },
        ],
      },
      // Workers: the workspace's open positions to apply to.
      {
        id: 'openings',
        label: 'nav.openings',
        icon: '@tui.briefcase',
        route: '/recruiting/openings',
        // schedule:read is every worker role's (employee, contractor,
        // volunteer); a personal account or no role sees none.
        access: {
          permissions: ['schedule:read'],
          apps: ['recruiting'],
          withoutPermissions: ['recruiting:manage'],
        },
      },
      {
        id: 'recruiting',
        label: 'nav.recruiting',
        icon: '@tui.megaphone',
        route: '/recruiting',
        access: { permissions: ['recruiting:manage'], apps: ['recruiting'] },
      },
      {
        id: 'payments',
        label: 'nav.payments',
        icon: '@tui.wallet',
        route: '/payments',
        access: { permissions: ['payments:read'], apps: ['payments'] },
        children: [
          {
            id: 'payments-requests',
            label: 'payments.tab.requests',
            icon: '@tui.banknote',
            route: '/payments/requests',
            access: { permissions: ['payments:manage'] },
          },
          {
            id: 'payments-history',
            label: 'payments.tab.history',
            icon: '@tui.history',
            route: '/payments/history',
          },
          // Payout Partner (PoP): one headed group in the Payments pane,
          // for payment managers whose workspace has payroll. Its pages keep
          // their /payroll URLs; clients and settings are the app's
          // configuration in the App Market (/my-apps/pop).
          {
            id: 'payout-partner',
            label: 'payroll.nav.section',
            icon: '@tui.handshake',
            route: '/payroll',
            access: { permissions: ['payments:manage'], apps: ['payments', 'pop'] },
            children: [
              {
                id: 'payroll-pay',
                label: 'payroll.tab.pay',
                icon: '@tui.clock',
                route: '/payroll/pay',
              },
              {
                id: 'payroll-payouts',
                label: 'payroll.tab.payouts',
                icon: '@tui.send',
                route: '/payroll/payouts',
              },
              {
                id: 'payroll-workers',
                label: 'payroll.tab.workers',
                icon: '@tui.users',
                route: '/payroll/workers',
              },
            ],
          },
        ],
      },
      {
        id: 'message-templates',
        label: 'nav.messageTemplates',
        icon: '@tui.send',
        route: '/notification-templates',
        access: { permissions: ['settings:manage'] },
        children: [
          {
            id: 'nt-events',
            label: 'nt.tabEvents',
            icon: '@tui.activity',
            route: '/notification-templates/events',
          },
          {
            id: 'nt-templates',
            label: 'nt.tabTemplates',
            icon: '@tui.layout-template',
            route: '/notification-templates/templates',
          },
          {
            id: 'nt-deliveries',
            label: 'nt.tabDeliveries',
            icon: '@tui.scroll-text',
            route: '/notification-templates/deliveries',
          },
        ],
      },
    ]),
  },
  {
    id: 'account',
    label: 'nav.account',
    items: [
      {
        id: 'profile',
        label: 'nav.profile',
        icon: '@tui.user',
        route: '/profile',
        children: [
          {
            id: 'profile-info',
            label: 'profile.tabInfo',
            icon: '@tui.user',
            route: '/profile/info',
          },
          {
            id: 'profile-emblems',
            label: 'profile.tabEmblems',
            icon: '@tui.award',
            route: '/profile/emblems',
          },
        ],
      },
      {
        id: 'my-apps',
        label: 'nav.myApps',
        icon: '@tui.layout-grid',
        route: '/my-apps',
      },
    ],
  },
];
